import useApi from "@hooks/useApi";
import Button from "../common/Button";
import Dib from "../common/Dib";
import Icon from "../common/Icon";
import { useAuth } from "@hooks/useAuth";
import { useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";

const TOAST_ID = "new-notifications";

const useNotificationCount = () => {
	const { data: unread } = useApi(
		"rpc/notifications",
		{
			select: "id",
			read: "is.false",
		},
		{
			schema: "portal",
			count: true,
			method: "GET",
			refreshInterval: 30000, // auto-reload every 30s
		}
	);
	const result = unread?._count || 0;
	return { unread: result };
};

export function NotificationsLinkView({ unread = 0 }) {
	return (
		<Button link to={`/notifications`} className="relative">
			<Icon name="bell" size="lg" />
			{unread > 0 && (
				<Dib
					count={unread}
					label="new notifications"
					position="top-2 right-1"
				/>
			)}
		</Button>
	);
}

function NotificationsLink() {
	const { user } = useAuth();
	const { unread } = useNotificationCount();
	const prevUnreadRef = useRef(null);
	const toastBaselineUnreadRef = useRef(null);

	const dismissNotificationsToast = () => {
		toastBaselineUnreadRef.current = null;
		toast.dismiss(TOAST_ID);
	};

	const showNotificationsToast = useCallback((delta) => {
		toast.custom(
			() => (
				<div
					className="bg-white rounded-xl shadow-md p-2 flex items-center gap-2 cursor-pointer group"
					onClick={dismissNotificationsToast}
					role="alert"
					tabIndex={0}
				>
					<div className="rounded-full w-7 h-7 border border-primary-blue flex items-center justify-center">
						<Icon
							name="bell"
							className="w-6 h-6 text-primary-blue"
						/>
					</div>
					{`You have ${delta} new notification${delta > 1 ? "s" : ""}`}
					<Icon
						name="cyber-close"
						className="rounded-full border w-6 h-6 group-hover:bg-primary-blue group-hover:text-white"
						aria-label="Close notification"
					/>
				</div>
			),
			{
				id: TOAST_ID,
				duration: Infinity,
				removeDelay: 50,
			}
		);
	}, []);

	useEffect(() => {
		if (prevUnreadRef.current === null && unread > 0) {
			prevUnreadRef.current = unread;
			return;
		}
		if (
			prevUnreadRef.current !== null &&
			prevUnreadRef.current !== unread
		) {
			if (unread > prevUnreadRef.current) {
				if (toastBaselineUnreadRef.current === null) {
					toastBaselineUnreadRef.current = prevUnreadRef.current;
				}
				const delta = unread - toastBaselineUnreadRef.current;
				showNotificationsToast(delta);
			}
			prevUnreadRef.current = unread;
		}
	}, [unread, showNotificationsToast]);

	useEffect(() => {
		return () => {
			toastBaselineUnreadRef.current = null;
			toast.dismiss(TOAST_ID);
		};
	}, []);

	if (!user) {
		return null;
	}
	return <NotificationsLinkView unread={unread} />;
}

export default NotificationsLink;
